import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiClient, ApiError } from '../api/client';
import type {
  InstallationDetail,
  InstallationFilter,
  InstallationType,
  Installer,
  SettledStatus,
} from '../api/types';
import { formatToman, toJalaliDateTime, toPersianDigits } from '../format';

/* Every installation, with the car and the customer attached.
 *
 * WHY THIS SCREEN EXISTS
 * ----------------------
 * Two questions had no answer anywhere in the panel: "what has this installer
 * actually done", and "which of those never finished".
 *
 * The second one matters more than it sounds. An installation is billed at
 * ACTIVATION, which happens before the vehicle reboots — deliberately, so that a
 * paid-for code never depends on ADB coming back afterwards. The cost of that
 * ordering is that a run which dies during the second phase still books a
 * charge: a car left on an empty launcher, an installer billed for it, and until
 * now nothing anywhere connecting the two. Those rows are what the "ناتمام"
 * filter shows, and deleting one is how the vehicle becomes installable again.
 */

const PAGE = 50;

const TYPE_LABEL: Record<InstallationType, string> = {
  INITIAL: 'نصب اولیه',
  RENEWAL: 'تمدید',
  REINSTALL: 'نصب مجدد',
};

const SETTLED_LABEL: Record<SettledStatus, string> = {
  UNSETTLED: 'تسویه‌نشده',
  DECLARED: 'اعلام‌شده',
  SETTLED: 'تسویه‌شده',
};

interface Props {
  api: ApiClient;
  onAuthLost: () => void;
}

export function InstallationsScreen({ api, onAuthLost }: Props) {
  const [rows, setRows] = useState<InstallationDetail[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [installers, setInstallers] = useState<Installer[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [installerId, setInstallerId] = useState<number | ''>('');
  const [type, setType] = useState<InstallationType | ''>('');
  const [settledStatus, setSettledStatus] = useState<SettledStatus | ''>('');
  const [incompleteOnly, setIncompleteOnly] = useState(false);
  const [search, setSearch] = useState('');
  /* The applied term, not the one being typed. Filtering on every keystroke
     would fire a request per character over a link that is often a phone
     tether. */
  const [appliedSearch, setAppliedSearch] = useState('');

  const [confirming, setConfirming] = useState<InstallationDetail | null>(null);
  /** Which settlement is being rejected from here, and why. */
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const filter: InstallationFilter = useMemo(
    () => ({
      installerId: installerId === '' ? undefined : installerId,
      type: type === '' ? undefined : type,
      settledStatus: settledStatus === '' ? undefined : settledStatus,
      incompleteOnly: incompleteOnly || undefined,
      search: appliedSearch || undefined,
      limit: PAGE,
      offset,
    }),
    [installerId, type, settledStatus, incompleteOnly, appliedSearch, offset],
  );

  const fail = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError && err.isUnauthenticated) return onAuthLost();
      setError(err instanceof ApiError ? err.message : 'خطای ناشناخته');
    },
    [onAuthLost],
  );

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const page = await api.installations(filter);
      setRows(page.rows);
      setTotal(page.total);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }, [api, filter, fail]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    /* Only for the filter dropdown. A failure here must not blank the screen —
     * the list is still perfectly usable without the names. */
    api.installers().then(setInstallers).catch(() => undefined);
  }, [api]);

  /* Any filter change starts again from the first page: staying on page 4 of a
     result set that now has one page shows an empty table and looks broken. */
  const resetTo = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setOffset(0);
  };

  const remove = async (row: InstallationDetail, reason: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.deleteInstallation(row.id, reason);
      setConfirming(null);
      setNotice(
        result.removedDevice
          ? 'نصب حذف شد و خودرو هم پاک شد — حالا می‌شود از اول نصب کرد.'
          : result.restoredSubscriptionId !== null
            ? 'تمدید حذف شد و اشتراک قبلی برگشت.'
            : 'نصب حذف شد.',
      );
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Approving or rejecting the settlement a row belongs to, without leaving.
   *
   * "Is this one paid?" and "approve it" were two screens apart: the answer was
   * here and the button was in the settlements tab, matched up by eye. Both act
   * on the whole settlement, not the row — a settlement is a single payment and
   * cannot be half approved — so the confirmation says so.
   */
  const decideSettlement = async (settlementId: number, approve: boolean, reason?: string) => {
    setBusy(true);
    setError(null);
    try {
      if (approve) {
        await api.approveSettlement(settlementId);
        setNotice('تسویه تایید شد.');
      } else {
        await api.rejectSettlement(settlementId, reason ?? '');
        setNotice('تسویه رد شد و نصب‌هایش دوباره قابل اعلام شدند.');
      }
      setRejecting(null);
      setRejectReason('');
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const incomplete = rows.filter((r) => r.completedAt === null).length;

  return (
    <div className="screen">
      <div className="card">
        <h3 style={{ marginTop: 0 }}>نصب‌ها</h3>

        <div className="filters">
          <label>
            <span>نصاب</span>
            <select
              value={installerId}
              onChange={(e) =>
                resetTo(setInstallerId)(e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              <option value="">همه</option>
              {installers.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.fullName}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>نوع</span>
            <select
              value={type}
              onChange={(e) => resetTo(setType)(e.target.value as InstallationType | '')}
            >
              <option value="">همه</option>
              {(Object.keys(TYPE_LABEL) as InstallationType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>تسویه</span>
            <select
              value={settledStatus}
              onChange={(e) => resetTo(setSettledStatus)(e.target.value as SettledStatus | '')}
            >
              <option value="">همه</option>
              {(Object.keys(SETTLED_LABEL) as SettledStatus[]).map((s) => (
                <option key={s} value={s}>
                  {SETTLED_LABEL[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={incompleteOnly}
              onChange={(e) => resetTo(setIncompleteOnly)(e.target.checked)}
            />
            <span>فقط ناتمام‌ها</span>
          </label>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setOffset(0);
              setAppliedSearch(search.trim());
            }}
          >
            <label>
              <span>جستجو</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="سریال، کد دستگاه، شاسی، نام یا موبایل مالک"
              />
            </label>
          </form>
        </div>

        {error && <div className="alert error">{error}</div>}
        {notice && <div className="alert">{notice}</div>}
        {incomplete > 0 && (
          <div className="alert warning">
            {toPersianDigits(incomplete)} نصب در این صفحه ناتمام مانده — یعنی مرحله دوم تأیید نشده و
            ممکن است خودرو روی لانچر خالی مانده باشد.
          </div>
        )}

        <table className="grid">
          <thead>
            <tr>
              <th>تاریخ</th>
              <th>وضعیت</th>
              <th>نوع</th>
              <th>نصاب</th>
              <th>خودرو</th>
              <th>مالک</th>
              <th>کل</th>
              <th>سهم نصاب</th>
              <th>سهم ما</th>
              <th>تسویه</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={row.completedAt === null ? 'row-warning' : undefined}>
                <td>{toJalaliDateTime(row.createdAt)}</td>
                <td>
                  {row.completedAt === null ? (
                    <span className="badge warning">ناتمام</span>
                  ) : (
                    <span className="badge ok">تکمیل‌شده</span>
                  )}
                </td>
                <td>
                  {TYPE_LABEL[row.type]}
                  {row.ownerChanged && <div className="muted">تغییر مالک</div>}
                </td>
                <td>{row.installer.fullName}</td>
                <td>
                  <div>{row.device.modelTitle ?? '—'}</div>
                  <div className="muted" dir="ltr">
                    {row.device.deviceCode}
                  </div>
                  {row.device.vin && (
                    <div className="muted" dir="ltr">
                      {row.device.vin}
                    </div>
                  )}
                </td>
                <td>
                  <div>{row.owner?.fullName ?? '—'}</div>
                  {row.owner && (
                    <div className="muted" dir="ltr">
                      {toPersianDigits(row.owner.phone)}
                    </div>
                  )}
                </td>
                {/* Three numbers, not one. "مبلغ" alone was the company's
                    share, which is the least interesting of the three to
                    everyone except us — an installer disputing a figure wants
                    to see what the customer paid and what they kept. */}
                <td>{row.customerPrice ? formatToman(row.customerPrice) : '—'}</td>
                <td>{row.installerShare ? formatToman(row.installerShare) : '—'}</td>
                <td>{formatToman(row.amount)}</td>
                <td>{SETTLED_LABEL[row.settledStatus]}</td>
                <td>
                  {/* Refused server-side once the row is in a live settlement;
                      disabled here too so the button is not an invitation to an
                      error message. */}
                  <div className="row">
                    {row.settledStatus === 'DECLARED' && row.settlementId !== null && (
                      <>
                        <button
                          className="primary"
                          disabled={busy}
                          title={`تایید کل تسویه ${row.settlementId}`}
                          onClick={() => void decideSettlement(row.settlementId!, true)}
                        >
                          تایید تسویه
                        </button>
                        <button
                          disabled={busy}
                          onClick={() => {
                            setRejecting(row.settlementId);
                            setRejectReason('');
                          }}
                        >
                          رد تسویه
                        </button>
                      </>
                    )}
                    <button
                      className="danger"
                      disabled={busy || row.settledStatus !== 'UNSETTLED'}
                      title={
                        row.settledStatus !== 'UNSETTLED'
                          ? 'این نصب در یک تسویه است. اول تسویه را رد کنید.'
                          : undefined
                      }
                      onClick={() => setConfirming(row)}
                    >
                      حذف
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !busy && (
              <tr>
                <td colSpan={11} className="muted">
                  چیزی یافت نشد.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="pager">
          <button disabled={busy || offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            قبلی
          </button>
          <span className="muted">
            {toPersianDigits(Math.min(offset + PAGE, total))} از {toPersianDigits(total)}
          </span>
          <button
            disabled={busy || offset + PAGE >= total}
            onClick={() => setOffset(offset + PAGE)}
          >
            بعدی
          </button>
        </div>
      </div>

      {rejecting !== null && (
        <div className="card">
          <div className="alert warning">
            رد کردن، کل تسویه {toPersianDigits(rejecting)} را برمی‌گرداند — نه فقط این یک نصب.
            نصب‌هایش دوباره قابل اعلام می‌شوند.
          </div>
          <label>
            <span>دلیل رد کردن — برای نصاب نمایش داده می‌شود</span>
            <textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="مثلاً: مبلغ واریزی کمتر از مجموع نصب‌های انتخاب‌شده بود"
              autoFocus
            />
          </label>
          <div className="actions">
            <button onClick={() => setRejecting(null)} disabled={busy}>
              انصراف
            </button>
            <button
              className="danger"
              disabled={busy || rejectReason.trim().length < 3}
              onClick={() => void decideSettlement(rejecting, false, rejectReason.trim())}
            >
              رد کردن تسویه
            </button>
          </div>
        </div>
      )}

      {confirming && (
        <ConfirmDelete
          row={confirming}
          busy={busy}
          onCancel={() => setConfirming(null)}
          onConfirm={(reason) => remove(confirming, reason)}
        />
      )}
    </div>
  );
}

/**
 * The delete dialog.
 *
 * It states what will actually disappear, because the answer differs by type and
 * "are you sure?" does not help anybody decide. A first activation takes the
 * vehicle with it — that is the point, since otherwise the retry would be
 * classified as a cheaper reinstall and hand back the old code — but it is not
 * something to discover afterwards.
 */
function ConfirmDelete({
  row,
  busy,
  onCancel,
  onConfirm,
}: {
  row: InstallationDetail;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  const consequence =
    row.type === 'INITIAL'
      ? 'خودرو، اشتراک و کدهایش هم حذف می‌شوند. بعد از این، نصب دوباره یک «نصب اولیه» کامل خواهد بود.'
      : row.type === 'RENEWAL'
        ? 'دوره‌ای که این تمدید ساخته حذف می‌شود و دوره قبلی دوباره فعال می‌شود. مشتری دقیقاً همان روزهایی را که قبلاً داشت نگه می‌دارد.'
        : 'فقط این صورتحساب حذف می‌شود. نصب مجدد چیزی نساخته بود.';

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="card modal" onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>حذف نصب</h3>
        <div className="alert warning">{consequence}</div>

        <dl className="pairs">
          <dt>خودرو</dt>
          <dd dir="ltr">{row.device.deviceCode}</dd>
          <dt>نصاب</dt>
          <dd>{row.installer.fullName}</dd>
          <dt>مبلغ</dt>
          <dd>{formatToman(row.amount)}</dd>
          <dt>تاریخ</dt>
          <dd>{toJalaliDateTime(row.createdAt)}</dd>
        </dl>

        <label>
          <span>دلیل حذف (در گزارش ثبت می‌شود)</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        </label>

        <div className="actions">
          <button onClick={onCancel} disabled={busy}>
            انصراف
          </button>
          <button
            className="danger"
            disabled={busy || reason.trim().length < 5}
            onClick={() => onConfirm(reason.trim())}
          >
            {busy ? 'در حال حذف…' : 'حذف کن'}
          </button>
        </div>
      </div>
    </div>
  );
}
